import { useDebouncedValue } from '@mantine/hooks';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { EnrichedBudgetEntry } from 'src/Components/Expenses/BudgetList/BudgetList.types';

export type BudgetFilterKind = 'expense' | 'income' | 'transfer';
export type BudgetFilterStatus = 'ALL' | 'SETTLED' | 'PENDING';

export interface BudgetFiltersState {
  search: string;
  mainCategories: string[];
  payerIds: string[];
  status: BudgetFilterStatus;
  kinds: BudgetFilterKind[];
  amountMin: number | null;
  amountMax: number | null;
  onlyMine: boolean;
}

export const EMPTY_BUDGET_FILTERS: BudgetFiltersState = {
  search: '',
  mainCategories: [],
  payerIds: [],
  status: 'ALL',
  kinds: [],
  amountMin: null,
  amountMax: null,
  onlyMine: false,
};

export interface BudgetPayerOption {
  id: string;
  name: string;
}

export interface BudgetFiltersSummary {
  count: number;
  total: number;
  myImpact: number;
}

interface UseBudgetFiltersResult {
  filters: BudgetFiltersState;
  setFilters: Dispatch<SetStateAction<BudgetFiltersState>>;
  resetFilters: () => void;
  filteredEntries: EnrichedBudgetEntry[];
  activeFilterCount: number;
  availableCategories: string[];
  availablePayers: BudgetPayerOption[];
  summary: BudgetFiltersSummary;
}

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Client-side filtering of the operations table. Everything it needs is already in memory
 * (useFetchExpenses walks every page of the year), so pushing this to the server would mean
 * rebuilding pagination, the merge with transfers and the virtual forecast rows for nothing.
 *
 * No persistence on purpose: filters left silently active across mounts turn into "some of
 * my expenses are missing" support tickets.
 */
const useBudgetFilters = (entries: EnrichedBudgetEntry[], year: number): UseBudgetFiltersResult => {
  const [filters, setFilters] = useState<BudgetFiltersState>(EMPTY_BUDGET_FILTERS);
  const [debouncedSearch] = useDebouncedValue(filters.search, SEARCH_DEBOUNCE_MS);

  const resetFilters = useCallback(() => setFilters(EMPTY_BUDGET_FILTERS), []);

  useEffect(() => {
    resetFilters();
  }, [year, resetFilters]);

  // Only the categories and payers actually present this year, so the bar never offers a
  // filter that can only ever return nothing.
  const availableCategories = useMemo(() => {
    const present = new Set<string>();
    for (const entry of entries) {
      if (entry.mainCategory) present.add(entry.mainCategory);
    }
    return [...present];
  }, [entries]);

  const availablePayers = useMemo(() => {
    const byId = new Map<string, string>();
    for (const entry of entries) {
      if (entry.payerId && entry.payerName) byId.set(entry.payerId, entry.payerName);
    }
    return [...byId]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const filteredEntries = useMemo(() => {
    const search = debouncedSearch.trim().toLowerCase();
    const isStatusFiltered = filters.status !== 'ALL';

    return entries.filter(entry => {
      if (filters.kinds.length > 0 && !filters.kinds.includes(entry.kind)) return false;

      if (filters.mainCategories.length > 0) {
        // Transfers carry neither a type nor a category: any category filter excludes them.
        if (!entry.mainCategory) return false;
        if (!filters.mainCategories.includes(entry.mainCategory)) return false;
      }

      if (filters.payerIds.length > 0) {
        if (!entry.payerId) return false;
        if (!filters.payerIds.includes(entry.payerId)) return false;
      }

      if (isStatusFiltered) {
        // A status filter is a question about what actually happened, so forecast rows,
        // which have no settlement status by construction, drop out of the answer.
        if (entry.kind !== 'transfer' && entry.isVirtual) return false;
        if (entry.status !== filters.status) return false;
      }

      if (filters.amountMin != null && entry.amount < filters.amountMin) return false;
      if (filters.amountMax != null && entry.amount > filters.amountMax) return false;

      if (filters.onlyMine && (entry.netImpact == null || entry.netImpact === 0)) return false;

      if (search) {
        const haystack = [entry.sortTitle, entry.payerName, entry.categoryLabel]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }

      return true;
    });
  }, [entries, filters, debouncedSearch]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search.trim()) count += 1;
    if (filters.mainCategories.length > 0) count += 1;
    if (filters.payerIds.length > 0) count += 1;
    if (filters.status !== 'ALL') count += 1;
    if (filters.kinds.length > 0) count += 1;
    if (filters.amountMin != null || filters.amountMax != null) count += 1;
    if (filters.onlyMine) count += 1;
    return count;
  }, [filters]);

  const summary = useMemo(() => {
    let total = 0;
    let myImpact = 0;
    for (const entry of filteredEntries) {
      total += entry.kind === 'income' ? entry.amount : -entry.amount;
      myImpact += entry.netImpact ?? 0;
    }
    return { count: filteredEntries.length, total, myImpact };
  }, [filteredEntries]);

  return {
    filters,
    setFilters,
    resetFilters,
    filteredEntries,
    activeFilterCount,
    availableCategories,
    availablePayers,
    summary,
  };
};

export default useBudgetFilters;
